import type { SemanticTone } from '../../components/ui';
import type {
  DeliveryResult,
  MailSettings,
  NotificationStatus,
  SubscriptionView,
  UpdateSubscriptionInput,
} from '../../types/notifications';
import { NOTIFICATION_KINDS } from '../../types/notifications';

/* ------------------------------------------------------------------ */
/* Delivery status                                                     */
/* ------------------------------------------------------------------ */

/** What actually happened to a message, in the words a person would use */
export const STATUS_LABEL: Record<NotificationStatus, string> = {
  sent: 'Delivered',
  failed: 'Failed',
  suppressed: 'Not sent',
};

/** Green only for what arrived; red for a real failure; "not sent" stays quiet */
export const statusTone = (status: NotificationStatus): SemanticTone =>
  status === 'sent' ? 'green' : status === 'failed' ? 'red' : 'neutral';

/** The fallback reason, when the server logged none */
export const STATUS_REASON: Record<NotificationStatus, string> = {
  sent: '',
  failed: 'The mail server refused it.',
  suppressed: 'Held back — quiet hours, or this kind is switched off.',
};

/* ------------------------------------------------------------------ */
/* Quiet hours                                                         */
/* ------------------------------------------------------------------ */

const HOUR = (hour: number) => `${String(hour).padStart(2, '0')}:00`;

export const HOURS: number[] = Array.from({ length: 24 }, (_, hour) => hour);

export const formatHour = HOUR;

/**
 * The whole setting in one sentence, with the exception said out loud — the
 * exception is what makes the setting trustworthy, so it is never a footnote.
 */
export function quietHoursSummary(from: number | null, to: number | null): string {
  if (from === null || to === null) return 'Notifications can arrive at any time of day.';
  return `Nothing between ${HOUR(from)} and ${HOUR(to)}, except critical alerts.`;
}

/** One short reason the window is refused, or null when it is fine */
export function validateQuietHours(from: number | null, to: number | null): string | null {
  if (from === null || to === null) return 'Pick a start and an end';
  if (!Number.isInteger(from) || !Number.isInteger(to)) return 'Whole hours only';
  if (from < 0 || from > 23 || to < 0 || to > 23) return 'Hours run from 00:00 to 23:00';
  if (from === to) return 'Start and end cannot be the same hour';
  return null;
}

/** How many hours the window covers, crossing midnight when it has to */
export function quietHoursLength(from: number, to: number): number {
  return to > from ? to - from : 24 - from + to;
}

/**
 * Quiet hours are one control to a person but one pair of columns per
 * subscription on the server. The first window found wins when they disagree.
 */
export function quietHoursOf(subscriptions: SubscriptionView[]): { from: number | null; to: number | null } {
  const set = subscriptions.find((sub) => sub.quietFrom !== null && sub.quietTo !== null);
  return { from: set?.quietFrom ?? null, to: set?.quietTo ?? null };
}

/** Every kind rewritten with the same window, leaving each switch as it was */
export function quietHoursPatch(
  subscriptions: SubscriptionView[],
  from: number | null,
  to: number | null,
): UpdateSubscriptionInput[] {
  return subscriptions.map((sub) => ({
    kind: sub.kind,
    channel: sub.channel,
    enabled: sub.enabled,
    quietFrom: from,
    quietTo: to,
  }));
}

/** The six kinds in contract order, with anything the server has not stored yet filled in as off */
export function subscriptionsInOrder(subscriptions: SubscriptionView[]): SubscriptionView[] {
  const byKind = new Map(subscriptions.map((sub) => [sub.kind, sub]));
  return NOTIFICATION_KINDS.map(
    (kind) =>
      byKind.get(kind) ?? {
        kind,
        channel: 'email' as const,
        enabled: false,
        quietFrom: null,
        quietTo: null,
      },
  );
}

/* ------------------------------------------------------------------ */
/* Mail                                                                */
/* ------------------------------------------------------------------ */

/** Mail can be saved and still send nothing; both halves have to be true */
export const mailReady = (settings: MailSettings | null): boolean =>
  !!settings && settings.enabled && !!settings.host.trim() && !!settings.fromAddress.trim();

/** What a run of the brief actually did, in one line */
export function deliverySummary(result: DeliveryResult): string {
  if (result.skippedReason) return result.skippedReason;
  const parts = [`${result.sent} sent`];
  if (result.suppressed) parts.push(`${result.suppressed} held back`);
  if (result.failed) parts.push(`${result.failed} failed`);
  if (!result.sent && !result.suppressed && !result.failed) return 'Nobody was due a brief just now';
  return parts.join(' · ');
}

/** "Sent in 412 ms", or the server's reason it could not */
export function checkSummary(check: { ok: boolean; message: string; latencyMs: number }): string {
  if (!check.ok) return check.message || 'The mail server refused the message';
  const timing = check.latencyMs > 0 ? ` in ${check.latencyMs} ms` : '';
  return `${check.message || 'Test message sent'}${timing}`;
}
